import React from 'react';
import { View } from 'react-native';
import { useResponsiveScale } from '../../../contexts/ResponsiveScaleContext';
import { observer } from 'mobx-react-lite';
import { PhotoPreviewBackground } from './PhotoPreviewBackground';
import { styles } from './PhotoPreview.styles';
import { PreviewContent } from '@photosi/rn-components/preview-content/preview-content/PreviewContent';
import { PreviewTexts } from '@photosi/rn-components/preview-content/preview-texts/PreviewTexts';
import { productPreviewTestID } from 'src/helpers/testIDs.helper';
import { TestIDs } from 'src/constants/testIDs.constants';
import Animated, {
  SharedValue,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { PreviewPrintModelInstance } from 'src/store/_models/previewPrint';
import { PreviewVintageModelInstance } from '../../../store/_models/previewVintage';
import { UserTextModelInstance } from '../../../store/_models/userText';

type PhotoPreviewProps = {
  preview: PreviewPrintModelInstance | PreviewVintageModelInstance;
  editable: boolean;
  showGrid: boolean;
  disabled?: boolean;
  disableSwapImageAnimation?: boolean;
  isHighlighted?: SharedValue<boolean> | boolean;
  showBadges?: boolean;
  userTexts: UserTextModelInstance[];
};

const PhotoPreview = observer(
  ({
    preview,
    disabled,
    editable,
    showGrid,
    disableSwapImageAnimation = false,
    isHighlighted = false,
    showBadges = true,
    userTexts = [],
  }: PhotoPreviewProps) => {
    const { responsiveScaleSV, responsiveScale } = useResponsiveScale();

    const borderAnimatedStyle = useAnimatedStyle(() => {
      if (typeof isHighlighted === 'object' && isHighlighted?.value !== undefined) {
        // SharedValue case - animated
        return {
          opacity: isHighlighted.value ? 1 : 0,
        };
      } else if (typeof isHighlighted === 'boolean') {
        // Boolean case - static
        return {
          opacity: isHighlighted ? 1 : 0,
        };
      }
      return {
        opacity: 0,
      };
    });
    const transformScaleSV = useSharedValue(1);
    useDerivedValue(() => {
      if (typeof isHighlighted === 'object' && isHighlighted?.value !== undefined) {
        // SharedValue case - animated
        transformScaleSV.value = withTiming(isHighlighted.value ? 0.98 : 1, { duration: 250 });
      } else if (typeof isHighlighted === 'boolean') {
        // Boolean case - static
        transformScaleSV.value = withTiming(isHighlighted ? 0.98 : 1, { duration: 250 });
      }
    }, [isHighlighted]);

    const sizeAnimatedStyle = useAnimatedStyle(
      () => ({
        width: preview.viewBox.width * responsiveScaleSV.value,
        height: preview.viewBox.height * responsiveScaleSV.value,
        transform: [{ scale: transformScaleSV.value }],
      }),
      [responsiveScaleSV, preview.viewBox.height, preview.viewBox.width, transformScaleSV, responsiveScale],
    );

    return (
      // PREVIEW CONTAINER
      <View
        style={[styles.previewContainer]}
        testID={productPreviewTestID(preview.userImageArea.productId, TestIDs.PHOTO_VINTAGE)}
      >
        <Animated.View style={[sizeAnimatedStyle]}>
          {/* Background vintage with basic effects */}
          <PhotoPreviewBackground
            viewBox={preview.backgroundViewBox}
            scaleSV={responsiveScaleSV}
            hasBorder={preview.hasBorder}
            color={preview.backgroundColor}
            zIndex={0}
          />

          {/* User Image Content */}
          <PreviewContent
            disabled={disabled}
            editable={editable}
            showGrid={showGrid}
            preview={preview}
            disableSwapImageAnimation={disableSwapImageAnimation}
            showBadges={showBadges}
          />

          <PreviewTexts preview={preview} userTexts={userTexts} />
        </Animated.View>
        {/* HIGHLIGHTED BORDER */}
        <Animated.View style={[styles.highlighted, sizeAnimatedStyle, borderAnimatedStyle]} />
      </View>
    );
  },
);

export default PhotoPreview;
